import React, { useState } from 'react';
import { Button, Input, message, Spin, Typography, Space, Card } from 'antd';
import { RobotOutlined, ThunderboltOutlined, CopyOutlined } from '@ant-design/icons';
import RichTextEditor from './RichTextEditor';
import RichTextRenderer from './RichTextRenderer';
import aiService from '../../services/aiService';

const { TextArea } = Input;
const { Text, Title } = Typography;

const AIDescriptionGenerator = ({
    menuItem = {},
    onDescriptionGenerated,
    placeholder = "Enter menu item details to generate description...",
    className = ""
}) => {
    const [loading, setLoading] = useState(false);
    const [generatedDescription, setGeneratedDescription] = useState('');
    const [inputDetails, setInputDetails] = useState('');

    const generateDescription = async () => {
        if (!inputDetails.trim() && !menuItem.name) {
            message.warning('Please provide menu item details or select a menu item');
            return;
        }

        setLoading(true);
        try {
            const prompt = `You are a professional food writer and marketing expert. Generate an appetizing, SEO-friendly description for a menu item.

MENU ITEM DETAILS:
${menuItem.name ? `Name: ${menuItem.name}` : ''}
${menuItem.category?.name ? `Category: ${menuItem.category.name}` : ''}
${menuItem.price ? `Price: ₹${menuItem.price}` : ''}
${Array.isArray(menuItem.ingredients) ? `Ingredients: ${menuItem.ingredients.join(', ')}` : ''}
${menuItem.is_vegetarian ? 'Dietary: Vegetarian' : 'Dietary: Non-vegetarian'}
${menuItem.is_spicy ? 'Spice Level: Spicy' : ''}
${menuItem.is_vegan ? 'Vegan: Yes' : ''}
${menuItem.is_gluten_free ? 'Gluten-Free: Yes' : ''}
${menuItem.preparation_time ? `Preparation Time: ${menuItem.preparation_time} minutes` : ''}

ADDITIONAL DETAILS:
${inputDetails}

REQUIREMENTS:
1. Write a compelling, mouth-watering description (2-3 sentences)
2. Include key ingredients and flavors
3. Mention dietary preferences (vegetarian, spicy, etc.)
4. Use appetizing food language
5. Keep it professional and appealing
6. Make it suitable for a food delivery platform
7. Include portion size or serving information if relevant

Generate a description that will make customers want to order this dish:`;

            const response = await aiService.chatWithAI(prompt, { type: 'description_generation' });
            
            if (response.success) {
                const description = response.data.trim();
                setGeneratedDescription(description);
                message.success('Description generated successfully!');
            } else {
                message.error('Failed to generate description. Please try again.');
            }
        } catch (error) {
            console.error('AI description generation error:', error);
            message.error('Failed to generate description. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedDescription).then(() => {
            message.success('Description copied to clipboard!');
        }).catch(() => {
            message.error('Failed to copy to clipboard');
        });
    };

    const useDescription = () => {
        if (onDescriptionGenerated) {
            onDescriptionGenerated(generatedDescription);
            message.success('Description applied to form!');
        }
    };

    return (
        <Card className={`ai-description-generator ${className}`} title={
            <Space>
                <RobotOutlined className="text-blue-500" />
                <span>AI Description Generator</span>
            </Space>
        }>
            <div className="space-y-4">
                {/* Input for additional details */}
                <div>
                    <Text strong>Menu Item Details (Optional)</Text>
                    <RichTextEditor
                        value={inputDetails}
                        onChange={setInputDetails}
                        placeholder={placeholder}
                        rows={3}
                    />
                </div>

                {/* Generate Button */}
                <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    onClick={generateDescription}
                    loading={loading}
                    block
                    size="large"
                    className="shadow-md hover:shadow-lg transition-all duration-200"
                    style={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        borderRadius: '8px'
                    }}
                >
                    {loading ? 'Generating Description...' : 'Generate AI Description'}
                </Button>

                {/* Generated Description */}
                {generatedDescription && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Text strong>Generated Description:</Text>
                            <Space>
                                <Button
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={copyToClipboard}
                                >
                                    Copy
                                </Button>
                                {onDescriptionGenerated && (
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={useDescription}
                                    >
                                        Use This Description
                                    </Button>
                                )}
                            </Space>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <RichTextRenderer content={generatedDescription} />
                        </div>
                    </div>
                )}

                {/* Tips */}
                <div className="bg-blue-50 p-3 rounded-lg">
                    <Text type="secondary" className="text-sm">
                        💡 <strong>Tip:</strong> Provide additional details like cooking method, 
                        special ingredients, or unique flavors to get a more personalized description.
                    </Text>
                </div>
            </div>
        </Card>
    );
};

export default AIDescriptionGenerator;
